import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import getRadioUpcomingAirings from '../../../api/graphql/queries/getRadioUpcomingAirings';
import { CHANNEL_OPTIONS } from '../../../utils/radioShowForm';
import { formatMadrid } from '../../../utils/radioTime';
import LoadIcon from '../../LoadIcon';
import { multilangLabel } from './RadioFields';

/*
What airs in the next N days, in Madrid time, grouped by Madrid day. pspanel's version
compared Madrid times against a UTC NOW() and was off by one or two hours; here the
backend works in UTC and the conversion happens only for display.
*/

const DAY_OPTIONS = [1, 3, 7, 14, 30];

const RadioUpcomingView = ({ refreshKey, onEdit, onAuthFail }) => {
  const [days, setDays] = useState(7);
  const [channel, setChannel] = useState('');
  const [airings, setAirings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const requestRef = useRef(0);

  useEffect(() => {
    requestRef.current += 1;
    const request = requestRef.current;
    setLoading(true);
    setLoadError('');
    getRadioUpcomingAirings({ days, channel: channel ? Number(channel) : undefined }).then((result) => {
      if (request !== requestRef.current) return;
      setLoading(false);
      if (!result) {
        setLoadError('Could not load the air times. Try again.');
        return;
      }
      if (!result.isAuth) {
        onAuthFail();
        return;
      }
      setAirings(result.airings || []);
    });
  }, [days, channel, refreshKey]);

  // The backend sorts by date; sort again so the day grouping never splits a day.
  const sorted = [...airings].sort((a, b) => String(a.date).localeCompare(String(b.date)));
  let currentDay = null;
  const rows = [];
  sorted.forEach((airing) => {
    const day = formatMadrid(airing.date, 'dddd DD/MM/YYYY');
    if (day !== currentDay) {
      currentDay = day;
      rows.push(
        <tr key={`day-${day}`} className="radio-upcoming-day">
          <td colSpan={6}>{day}</td>
        </tr>,
      );
    }
    rows.push(
      <tr key={airing.id}>
        <td>{formatMadrid(airing.date, 'HH:mm')}</td>
        <td>{airing.channel}</td>
        <td>
          {multilangLabel(airing.title, airing.slug || `#${airing.showId}`)}
          {airing.status === 'oculta' ? <span className="radio-badge is-hidden" style={{ marginLeft: 6 }}>Hidden</span> : null}
        </td>
        <td>{multilangLabel(airing.parentTitle, '—')}</td>
        <td className="radio-help" style={{ color: '#646464' }}>{airing.slug}</td>
        <td>
          <div className="radio-table-actions">
            <button type="button" className="radio-light-button is-ghost" onClick={() => onEdit('episodes', airing.showId)}>
              Edit
            </button>
          </div>
        </td>
      </tr>,
    );
  });

  return (
    <div>
      <div className="flex justify-between mb-4">
        <span className="text-americaMonoBold text-26">Upcoming airings</span>
      </div>
      <div className="radio-filters" style={{ marginTop: 0 }}>
        <label htmlFor="radio-upcoming-days">
          Next
          <select id="radio-upcoming-days" value={days} onChange={event => setDays(Number(event.target.value))}>
            {DAY_OPTIONS.map(option => <option key={option} value={option}>{`${option} day${option === 1 ? '' : 's'}`}</option>)}
          </select>
        </label>
        <label htmlFor="radio-upcoming-channel">
          Channel
          <select id="radio-upcoming-channel" value={channel} onChange={event => setChannel(event.target.value)}>
            <option value="">Both</option>
            {CHANNEL_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
        <span className="radio-help" style={{ color: '#646464', paddingBottom: 8 }}>Times are Madrid time (Europe/Madrid).</span>
      </div>
      {loading ? <LoadIcon /> : null}
      {!loading && loadError ? <p className="radio-error-text">{loadError}</p> : null}
      {!loading && !loadError && !sorted.length ? <p className="radio-empty">Nothing scheduled in this period.</p> : null}
      {!loading && !loadError && sorted.length ? (
        <table className="radio-upcoming-table">
          <thead>
            <tr>
              <th>time</th>
              <th>channel</th>
              <th>show</th>
              <th>program</th>
              <th>slug</th>
              <th />
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      ) : null}
    </div>
  );
};

RadioUpcomingView.propTypes = {
  refreshKey: PropTypes.number,
  // (kind, showId): opens the show form. The form works out from the show itself whether
  // it is a program or an episode.
  onEdit: PropTypes.func.isRequired,
  onAuthFail: PropTypes.func.isRequired,
};

RadioUpcomingView.defaultProps = {
  refreshKey: 0,
};

export default RadioUpcomingView;
